import axios from 'axios'
import { getAuthToken } from 'utils/authHeaders'
import { isSessionExpiry, loginUrlPreserving } from 'utils/sessionExpiry'

/**
 * Tear down an expired session and send the user to sign in again.
 *
 * A hard navigation rather than a router push, deliberately. The store cannot be
 * imported here — that is the circular import described below — and a full page
 * load is the one thing guaranteed to clear every slice's in-memory copy of the
 * dead session. localStorage is cleared first so the synchronous hydration in
 * `authSlice` does not simply restore it on the way back up.
 *
 * Guarded by a module-level flag because a screen typically fires several
 * requests at once; without it, one expiry would trigger a redirect per
 * in-flight call.
 */
let sessionEnding = false
// Exported for the one caller that cannot use axios: streaming over fetch
// (services/aiService.ts, MYD-625), which must end a dead session the same way.
export const endSession = () => {
	if (sessionEnding) return
	sessionEnding = true
	try {
		localStorage.removeItem('userInfo')
	} catch {
		/* Private browsing can throw. The redirect still matters more. */
	}
	if (typeof window !== 'undefined') {
		window.location.assign(loginUrlPreserving(window.location.pathname + window.location.search))
	}
}

/**
 * Creates an axios instance that attaches the session token to every request.
 *
 * The token is read from localStorage rather than from the Redux store, which
 * breaks a circular import (DT-029):
 *
 *   axiosInterceptor -> store -> slices/index -> every slice
 *     -> incomeStatementSlice -> incomeStatementService
 *     -> axiosInterceptor   (not yet initialized)
 *
 * That cycle resolved to `undefined` and threw "axiosInterceptor is not a
 * function" whenever a slice was imported outside the app's usual entry order —
 * which is why every slice unit test needed a `vi.mock('services')` stub simply
 * to load. The app itself survived only because CRA's module evaluation order
 * happened to initialize the export before the consumer ran; a bundler change,
 * a new import edge, or a different entry point could break it.
 *
 * Reading localStorage is not a downgrade. `authSlice` writes `userInfo` there
 * on every path that produces a token — login, magic link, registration, email
 * verification, profile patch, trial conversion — and removes it on logout. The
 * support-impersonation screen writes it directly, so for that flow localStorage
 * is in fact the more reliable source. The store itself hydrates from it on
 * first load.
 *
 * The token is still read per request, not captured at module load, so a login
 * or logout mid-session is picked up on the next call exactly as before.
 */
const axiosInterceptor = baseURL => {
	const interceptor = axios.create({ baseURL })

	interceptor.interceptors.request.use(
		config => {
			const token = getAuthToken()

			if (token) {
				config.headers.Authorization = `Bearer ${token}`
			}
			return config
		},
		error => {
			return Promise.reject(error)
		},
	)

	interceptor.interceptors.response.use(
		response => {
			return response
		},
		error => {
			if (isSessionExpiry(error)) endSession()
			return Promise.reject(error)
		},
	)

	return interceptor
}

export default axiosInterceptor
